// src/services/benchmarkBandsService.js
//
// LOCATIONS-ASSIGN-BAR-S1: Client-side service for benchmark band configuration.
//
// Fetches /api/admin/benchmark-bands?metric_key=assignment_pct, caches the
// result for 5 minutes (matching the locationCache.js TTL pattern), and
// exposes:
//   getBands()     — async; returns the cached sorted bands array
//   classify(val)  — sync; returns {color, label} for the matching band,
//                    or null when value is null/undefined or bands are empty
//
// On fetch failure: logs [Error] and returns [] — never throws.

import { log } from "../utils/logger.js";

const TTL_MS = 5 * 60 * 1000; // 5 minutes — matches locationCache.js

// Module-level singleton cache. Reset via __resetForTests() in tests.
let _cachedBands = [];
let _expiresAt   = 0;

/**
 * Fetch and return the assignment_pct benchmark bands, sorted by bandOrder.
 * Result is cached for TTL_MS. On error, logs and returns [].
 *
 * @returns {Promise<Array<{bandOrder:number, maxValue:number|null, color:string, label:string}>>}
 */
export async function getBands() {
  if (Date.now() < _expiresAt) {
    return _cachedBands;
  }

  try {
    const res = await fetch("/api/admin/benchmark-bands?metric_key=assignment_pct");
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    const sorted = (data.bands ?? [])
      .slice()
      .sort((a, b) => a.bandOrder - b.bandOrder);

    if (sorted.length === 0) {
      log.error("[Error] benchmarkBandsService: empty bands for assignment_pct - bars will render neutral");
    }

    _cachedBands = sorted;
    _expiresAt   = Date.now() + TTL_MS;
  } catch (err) {
    log.error(`[Error] benchmarkBandsService: failed to load bands - ${err.message}`);
    _cachedBands = [];
    _expiresAt   = 0; // don't cache failures; retry next call
  }

  return _cachedBands;
}

/**
 * Classify a numeric value against the currently cached bands.
 *
 * Walks bands in bandOrder ascending order. Returns the first band where
 * value <= maxValue, or the open-ended band (maxValue = null) if no
 * capped band matches.
 *
 * @param {number|string|null|undefined} value  — string inputs are coerced to number
 * @returns {{ color: string, label: string } | null}
 *   null when value is null/undefined, or when no bands are cached.
 */
export function classify(value) {
  if (value == null)           return null;
  if (_cachedBands.length === 0) return null;

  const num = Number(value);

  for (const band of _cachedBands) {
    if (band.maxValue === null || band.maxValue === undefined) {
      return { color: band.color, label: band.label };
    }
    if (num <= Number(band.maxValue)) {
      return { color: band.color, label: band.label };
    }
  }

  // Safety fallback: return last band if no null-capped band was present.
  const last = _cachedBands[_cachedBands.length - 1];
  return { color: last.color, label: last.label };
}

/** Test-only: reset module-level cache state. */
export function __resetForTests() {
  _cachedBands = [];
  _expiresAt   = 0;
}
